import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useThrottleFn } from 'react-use';

import { authActions, authSelectors } from 'app-components/flux/auth';
import { bumpActions, bumpInterface, bumpSelectors, bumpTypes } from 'app-components/flux/bump';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import { getIsOffline } from 'data/app/selectors';

const BumpConnector = () => {
  const authSession = useSelector(authSelectors.getAuthSession);
  const bumpRequireAuthentication = useSelector(bumpSelectors.doesBumpRequireAuthentication);
  const bumpRequiresOpening = useSelector(bumpSelectors.doesBumpRequireOpening);
  const featureFlagsError = useSelector(featureFlagsSelectors.getError);
  const featureFlagsLoadPending = useSelector(featureFlagsSelectors.getLoadPending);
  const isOnline = !useSelector(getIsOffline);
  const bumpEnabled = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'webSocketQcs'));

  const dispatch = useDispatch();

  const openRequired = Boolean(
    bumpRequiresOpening && authSession.datasetId && authSelectors.isAccessTokenValid(authSession) && isOnline,
  );
  const reconnectTimeout = bumpInterface.reconnectTimeout();
  const openRequiredThrottled = useThrottleFn(
    (openRequiredNew) => openRequiredNew && DateTime.local(),
    reconnectTimeout,
    [openRequired],
  ); // time ensures the trigger after throttle

  useEffect(() => {
    if (!featureFlagsLoadPending || featureFlagsError) {
      if (bumpEnabled) {
        if (openRequiredThrottled) {
          bumpInterface.open('Open from React UseEffect');
        }
      } else {
        bumpInterface.close(bumpTypes.CLOSE_BUMP_DISABLED, 'Close from React UseEffect');
      }
    }
  }, [bumpEnabled, featureFlagsError, featureFlagsLoadPending, dispatch, openRequiredThrottled]);

  useEffect(() => {
    if (bumpRequireAuthentication) {
      const isAccessTokenValid = authSelectors.isAccessTokenValid(authSession);
      if (authSession.datasetId && isAccessTokenValid) {
        dispatch(
          bumpActions.sendBumpMessage({
            qcsDatasetId: authSession.datasetId,
            token: `Bearer ${authSession.accessToken}`,
          }),
        );
      } else if (!isAccessTokenValid && authSelectors.isRefreshTokenValid(authSession)) {
        dispatch(authActions.exchangeRefreshToken());
      }
    }
  }, [authSession, bumpRequireAuthentication, dispatch]);

  return null;
};

export default BumpConnector;
